import { SxStyle } from '@aibees/shared/theme';
import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { CustomDialog } from '../custom-dialog';
import { Icon } from '../icon';
import { useVideoPlayer } from './hooks';
import * as Styled from './styles';
import * as SX from './sx';
import type { VideoModalProps as Props } from './types';

const DEFAULT_LOAD_TIMEOUT_MS = 12_000;

export const VideoModal = memo(
  ({
    className,
    state,
    videoUrl,
    title,
    autoplay = true,
    loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
    contentSx,
    onOpen,
    onClose,
  }: Props) => {
    const { resolved, phase, handleLoad, handleError } = useVideoPlayer({
      videoUrl,
      isOpen: state.value,
      autoplay,
      loadTimeoutMs,
    });

    const accessibleTitle =
      typeof title === 'string' && title ? title : 'Video player';

    const handleOpenInNewTab = () => {
      window.open(videoUrl, '_blank', 'noopener,noreferrer');
    };

    return (
      <CustomDialog
        className={classnames('VideoModal', className)}
        state={state}
        title={title}
        maxWidth="md"
        contentSx={{ ...SX.content, ...contentSx } as SxStyle}
        onOpen={onOpen}
        onClose={onClose}
        PaperProps={{ sx: { width: '100%' } }}
      >
        <Styled.Frame sx={SX.aspectFrame}>
          {state.value && resolved && phase !== 'error' && (
            <Styled.Player
              key={resolved.embedUrl}
              src={resolved.embedUrl}
              title={accessibleTitle}
              allow={resolved.provider.iframeAllow}
              sandbox={resolved.provider.iframeSandbox}
              referrerPolicy="strict-origin-when-cross-origin"
              loading="lazy"
              allowFullScreen
              onLoad={handleLoad}
              onError={handleError}
              sx={SX.iframe}
            />
          )}

          {state.value && phase === 'loading' && (
            <Box sx={SX.overlay}>
              <CircularProgress size={32} sx={SX.spinner} />
            </Box>
          )}

          {phase === 'error' && (
            <Stack sx={SX.errorOverlay}>
              <Icon name="warning-amber" size={40} color="grey.500" />
              <Stack alignItems="center" gap={0.5}>
                <Typography variant="body2" color="grey.800">
                  Video couldn&apos;t load
                </Typography>
                <Typography variant="body3" color="grey.600">
                  The embed may be blocked. You can open it in a new tab.
                </Typography>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleOpenInNewTab}
                startIcon={<Icon name="open-in-new" size={16} />}
              >
                Open in new tab
              </Button>
            </Stack>
          )}
        </Styled.Frame>
      </CustomDialog>
    );
  }
);

VideoModal.displayName = 'VideoModal';
